import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { EntitySelect } from '@/components/EntitySelect';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import { MemberSelect } from '@/features/members';
import { LOGGABLE_STATE_GROUPS } from '../constants';
import {
  makeWorkItemSearch,
  resolveProjectLabel,
  resolveWorkItemLabel,
  searchProjects,
} from '../select';
import { formatMinutes, parseMinutes } from '../timeFormat';
import type { TimeEntry, TimeEntryInput } from '../types';

const schema = z.object({
  project: z.string().min(1, 'Project is required'),
  user: z.string(),
  work_item: z.string().min(1, 'Work item is required'),
  date: z.string().min(1, 'Date is required'),
  // The form field stays free-text; it's parsed to whole minutes on submit.
  hours: z.string().refine((v) => parseMinutes(v) !== null, 'Enter time, e.g. 1h 30min or 1.5'),
  description: z.string(),
  billable: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

// Note-only edit (manual logging off): the other fields are disabled and not
// sent, so they mustn't block the save — a project-level worklog has no work
// item, and "Work item is required" under a disabled picker would never clear.
const noteOnlySchema = schema.extend({
  project: z.string(),
  work_item: z.string(),
  date: z.string(),
  hours: z.string(),
});
const FIELD_KEYS = [
  'project',
  'user',
  'work_item',
  'date',
  'hours',
  'description',
  'billable',
] as const;

interface TimeEntryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry?: TimeEntry;
  onSubmit: (input: TimeEntryInput) => Promise<unknown>;
  /** Founder/Super Admin only — others are pinned to themselves (ADR-077). */
  canLogForOthers?: boolean;
  /** Manual logging is off for this member: the time is what the timer captured,
   *  only the note is editable (the API refuses any other change). */
  noteOnly?: boolean;
}

function toValues(entry?: TimeEntry): FormValues {
  return {
    project: entry?.project ? String(entry.project) : '',
    user: entry?.user ? String(entry.user) : '',
    work_item: entry?.work_item ? String(entry.work_item) : '',
    date: entry?.date ?? new Date().toISOString().slice(0, 10),
    hours: entry ? formatMinutes(entry.minutes) : '',
    description: entry?.description ?? '',
    billable: entry?.billable ?? true,
  };
}

export function TimeEntryFormDialog({
  open,
  onOpenChange,
  entry,
  onSubmit,
  canLogForOthers = true,
  noteOnly = false,
}: TimeEntryFormDialogProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(noteOnly ? noteOnlySchema : schema),
    defaultValues: toValues(entry),
  });

  const { reset } = form;
  // Track the project the Logger/Work-item pickers are currently scoped to, so a
  // *user-initiated* project change clears those picks (they belong to the old
  // project) — without clobbering the values we prefill on open (edit mode).
  const scopedProject = useRef<string>(entry?.project ? String(entry.project) : '');
  useEffect(() => {
    if (open) {
      reset(toValues(entry));
      scopedProject.current = entry?.project ? String(entry.project) : '';
    }
  }, [open, entry, reset]);

  const projectValue = useWatch({ control: form.control, name: 'project' });
  const projectId = projectValue ? Number(projectValue) : undefined;
  const { setValue } = form;
  useEffect(() => {
    if (projectValue !== scopedProject.current) {
      scopedProject.current = projectValue;
      setValue('user', '');
      setValue('work_item', '');
    }
  }, [projectValue, setValue]);

  // Work-item picker is scoped to the selected project and to loggable states
  // (started / completed) — you can't log against not-yet-started or cancelled
  // work. No-op search until a project is chosen (the field is disabled then).
  const workItemSearch = useMemo(
    () =>
      projectId != null
        ? makeWorkItemSearch(projectId, { stateGroups: LOGGABLE_STATE_GROUPS })
        : async () => [],
    [projectId],
  );

  async function handleSubmit(values: FormValues) {
    const input: TimeEntryInput = {
      project: Number(values.project),
      user: values.user ? Number(values.user) : null,
      work_item: Number(values.work_item),
      date: values.date,
      // Whole minutes is the stored unit — no decimal-hours round-trip, so
      // 20/40-minute entries stay exact and timesheet totals don't drift.
      minutes: parseMinutes(values.hours) ?? 0,
      description: values.description,
      billable: values.billable,
    };
    try {
      await onSubmit(input);
      onOpenChange(false);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          // The API field is `minutes`; the form input is the free-text `hours`.
          const name = field === 'minutes' ? 'hours' : field;
          const key = FIELD_KEYS.find((k) => k === name);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{noteOnly ? 'Edit note' : entry ? 'Edit time entry' : 'Log time'}</DialogTitle>
          <DialogDescription>
            {noteOnly
              ? 'Manual time logging is off — the time stays as the timer recorded it; only the note can be changed.'
              : 'Record time worked on a work item.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          {/* min-w-0: DialogContent is a grid — without it the form (grid item,
              min-width:auto) grows to the widest nowrap label (long work-item
              titles) and every w-full field overflows the dialog. */}
          <form onSubmit={form.handleSubmit(handleSubmit)} className="min-w-0 space-y-6">
            <FormSection title="Work">
              <FormField
                control={form.control}
                name="project"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Project</FormLabel>
                    <FormControl>
                      <EntitySelect
                        queryKey="time-entry-project-picker"
                        value={field.value}
                        onChange={field.onChange}
                        search={searchProjects}
                        resolveLabel={resolveProjectLabel}
                        placeholder="Select a project"
                        disabled={noteOnly}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {canLogForOthers ? (
                <FormField
                  control={form.control}
                  name="user"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Logger</FormLabel>
                      <FormControl>
                        <MemberSelect
                          queryKey="time-entry-user-picker"
                          project={projectId}
                          disabled={!projectId || noteOnly}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder={projectId ? 'Me' : 'Select a project first'}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}

              <FormField
                control={form.control}
                name="work_item"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Work item</FormLabel>
                    <FormControl>
                      <EntitySelect
                        queryKey={`time-entry-workitem-${projectId ?? 'none'}`}
                        value={field.value}
                        onChange={field.onChange}
                        search={workItemSearch}
                        resolveLabel={resolveWorkItemLabel}
                        placeholder={projectId ? 'Select a work item' : 'Select a project first'}
                        disabled={!projectId || noteOnly}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

            </FormSection>

            <FormSection title="Time">
              {/* Date + duration are a pair — keep them on one row. */}
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date</FormLabel>
                      <FormControl>
                        <Input type="date" disabled={noteOnly} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="hours"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Duration</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          inputMode="text"
                          placeholder="e.g. 1h 30min"
                          disabled={noteOnly}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="billable"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between gap-2 rounded-md border px-3 py-2">
                    <FormLabel className="m-0 font-normal">Billable</FormLabel>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={noteOnly}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea rows={2} placeholder="What did you work on?" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>

            {form.formState.errors.root ? (
              <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Saving…' : entry ? 'Save changes' : 'Log time'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
